import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { EMPTY, forkJoin, of } from 'rxjs'
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators'
import { LegacyApiService } from '../../../legacy/services/legacy-api.service'
import { AppState } from '../index'
import * as LegacyActions from './legacy.actions'
import { selectSelectedLegacyClientId } from './legacy.selectors'

@Injectable()
export class LegacyEffects {
  private readonly actions$ = inject(Actions)
  private readonly store = inject(Store<AppState>)
  private readonly api = inject(LegacyApiService)

  loadActiveContext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LegacyActions.loadActiveContext),
      switchMap(() =>
        this.api.getActiveContext().pipe(
          withLatestFrom(this.store.select(selectSelectedLegacyClientId)),
          map(([context, currentClientId]) =>
            currentClientId
              ? LegacyActions.markActiveContextLoaded()
              : LegacyActions.activeContextLoaded({
                  clientId: context.client_id,
                  yearId: context.year_id,
                  programId: context.program_id,
                  structureId: context.structure_id,
                }),
          ),
          catchError(() => of(LegacyActions.markActiveContextLoaded())),
        ),
      ),
    ),
  )

  resolveContextNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LegacyActions.setSelectedProgram, LegacyActions.activeContextLoaded),
      switchMap(({ clientId, yearId, programId }) =>
        forkJoin({
          client: clientId
            ? this.api.getClient(clientId).pipe(catchError(() => of(null)))
            : of(null),
          year: yearId ? this.api.getYear(yearId).pipe(catchError(() => of(null))) : of(null),
          program: programId
            ? this.api.getProgram(programId).pipe(catchError(() => of(null)))
            : of(null),
        }).pipe(
          map(({ client, year, program }) =>
            LegacyActions.setActiveContextNames({
              clientName: client?.name ?? null,
              yearLabel: year ? String(year.year) : null,
              programName: program?.name ?? null,
            }),
          ),
        ),
      ),
    ),
  )

  // Persist the active context ONLY when the user explicitly applies a
  // selection (via a dialog), never on plain navigation / route restores.
  persistActiveContext$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LegacyActions.applyActiveContext),
        switchMap(({ clientId, yearId, programId, structureId }) =>
          this.api
            .saveActiveContext({
              client_id: clientId,
              year_id: yearId,
              program_id: programId,
              structure_id: structureId,
            })
            .pipe(catchError(() => EMPTY)),
        ),
      ),
    { dispatch: false },
  )
}
